import { createTheme } from '@mui/material/styles';

import { Theme } from 'common/types/theme.type';

const defaultTheme: Theme = {
  colors: {
    mainBg: '#FFFFFF',
    footerBg: '#FBFBFB',
    selectionBgSmooth:
      'linear-gradient(270deg, rgb(48 126 225) 0%, rgb(45 119 212) 6.25%, rgb(42 112 199) 12.5%, rgb(39 105 186) 18.75%, rgb(36 98 173) 25%, rgb(33 91 160) 31.25%, rgb(30 84 147) 37.5%, rgb(27 77 134) 43.75%, rgb(24 70 121) 50%, rgb(21 63 108) 56.25%, rgb(18 56 95) 62.5%, rgb(15 49 82) 68.75%, rgb(12 42 69) 75%, rgb(9 35 56 / 43%) 81.25%, rgb(6 28 43 / 26%) 87.5%, rgb(3 21 30 / 22%) 93.75%, rgb(0 14 17 / 0%) 100%)',
    innerStroke: '#DCE0E8',
    primaryGrey: '#FDFDFF',
    primaryGrey2: '#606C87',
    primaryGrey3: '#8493B7',
    primaryWhite: '#FFFFFF',
    secondaryGrey: '#CBD9FF',
    primaryStroke: '#EAEFF7',
    selectedField: '#F0F3F8',
    secondaryStroke: '#BFC8D6',
    chips: '#E2E5EF',
    chips2: '#F7F9FC',
    severity0: '#75CB73',
    severity1: '#FFE975',
    severity2: '#FFB800',
    severity3: '#FF8A01',
    severity4: '#FF2C01',
    severity5: '#DF0101',
    fortinetRed: '#EE2E24',
    primaryBlue1: '#252A3D',
    primaryBlue2: '#5C5F70',
    primaryBlue3: '#F1F2F5',
    primaryBlue4: '#AAB7CA',
    primaryBlue5: '#40445B',
    primaryBlue6: '#325A92',
    primaryBlue7: '#F1F8FF',
    primaryBlue8: '#87AFDC',
    primaryBlue9: '#FBFCFF',
    primaryBlue10: '#0374DF',
    primaryBlue11: '#EFF6FF',
    primaryBlue12: '#ABCEFF',
    primaryBlue13: '#F6F9FC',
    primaryBlue14: '#0077ff',
    blue: '#145AEA',
    info: '#5DB6FF',
    cyan: '#A7FFEF',
    error: '#D83B48',
    error2: '#D32F2F',
    violet: '#F9B7FF',
    success: '#75CB73',
    lightRed: '#FFCFC5',
    scrollBg: '#ECEDF2',
    linkBlue: '#5D83D8',
    disabled1: '#DADBDE',
    disabled2: '#EBECEF',
    disabled3: '#F0F0F1',
    disabled4: '#B0B0B1',
    burlyWood: '#F7CEA8',
    lightPink: '#F7C8E0',
    paleGreen: '#CBF7A8',
    available: '#C674CC',
    circleBlue: '#267CB5',
    lightGreen: '#CDFFCC',
    unavailable: '#D98657',
    scrollThumb: '#9499B6',
    suridataRed: '#FF2C01',
    primaryGrey4: '#f1f2f580',
    lightSteelBlue: '#A8C5F7',
    statusChartPurple: '#C37FE2',
    workflowActionOrange: '#F09056',
    workflowTriggerPurple: '#463CBB',
    workflowConditionGreen: '#5DBAC3',
  },
  fonts: {
    fontFamily: 'Mulish',
    defaultFontSize: '13px',
  },
  sizes: {
    defaultBorderRadius: '4px',
  },
};

export const muiTheme = createTheme({
  typography: {
    fontSize: 13,
    fontFamily: defaultTheme.fonts.fontFamily,
  },
  components: {
    MuiButtonBase: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          margin: '0.5rem 0',
          backgroundColor: defaultTheme.colors.disabled1,
        },
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        columnHeader: {
          outline: 'none !important',
        },
        cell: {
          display: 'flex',
          alignItems: 'center',
          outline: 'none !important',
        },
        paper: {
          maxHeight: 250,
        },
        root: {
          outline: 'none !important',
          backgroundColor: defaultTheme.colors.primaryWhite,
        },
        row: {
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          color: defaultTheme.colors.primaryBlue1,
          '&.Mui-checked': {
            color: defaultTheme.colors.primaryBlue1,
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        list: {
          fontSize: 13,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontSize: 13,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        message: {
          display: 'flex',
          alignItems: 'center',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: 13,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: 13,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        fullWidth: true,
        margin: 'dense',
        variant: 'outlined',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          '&[type=number]': {
            fontSize: 13,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          marginRight: 0,
          fontSize: 11.3,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        arrow: {
          color: defaultTheme.colors.primaryWhite,
          '&:before': {
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
            border: `1px solid ${defaultTheme.colors.secondaryStroke}`,
          },
        },
        tooltip: {
          display: 'flex',
          fontWeight: 400,
          flexDirection: 'column',
          color: defaultTheme.colors.primaryBlue1,
          fontFamily: defaultTheme.fonts.fontFamily,
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          fontSize: defaultTheme.fonts.defaultFontSize,
          backgroundColor: defaultTheme.colors.primaryWhite,
          border: `1px solid ${defaultTheme.colors.secondaryStroke}`,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableFocusRipple: true,
      },
      styleOverrides: {
        root: {
          fontSize: 13,
          textTransform: 'none',
          borderRadius: '0.25rem',
        },
        text: {
          color: defaultTheme.colors.primaryBlue1,
        },
        contained: {
          backgroundColor: defaultTheme.colors.primaryBlue1,
          '&.Mui-disabled': {
            backgroundColor: defaultTheme.colors.disabled1,
          },
        },
        outlined: {
          color: defaultTheme.colors.primaryBlue1,
          borderColor: defaultTheme.colors.primaryBlue1,

          '&.Mui-disabled': {
            color: defaultTheme.colors.disabled1,
            borderColor: defaultTheme.colors.disabled1,
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '0px 1.5rem 1.5rem',
        },
      },
    },
    MuiRadio: {
      defaultProps: {
        size: 'small',
        disableRipple: true,
        disableTouchRipple: true,
      },
      styleOverrides: {
        root: {
          color: defaultTheme.colors.primaryBlue1,
          '&.Mui-checked': {
            color: defaultTheme.colors.primaryBlue1,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          width: '100%',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          width: '100%',
        },
      },
    },
  },
} as any);

export default defaultTheme;
